import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { formatEatTime } from '@wodi/domain';
import { Button, CriticalStrip, Dialog, DialogActions, StatusStrip, Tag } from '@wodi/ui';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { useOnline } from '../hooks/use-online.ts';
import { translate, useT, type Translate } from '../i18n/i18n.tsx';
import { sweepExpiredDrafts } from '../patients/drafts.ts';
import { toasts } from '../toasts.ts';
import { EXPIRED_ERROR, retryQueue, type QueuedAction, type QueuedKind } from './retry-queue.ts';
import styles from './offline.module.css';

// What the person sees of drafts and the retry queue (DESIGN.md §7 Offline indicator and
// Integration status; ADR 0018): an ink strip while offline saying drafts stay on this
// device and how many actions will send, a strip while sending, and a strip with "Review"
// when something could not be sent, listing each item's state, reason and next step.

/** How often waiting items are tried again while online. */
export const RETRY_INTERVAL_MS = 20_000;

export function useRetryQueue(): readonly QueuedAction[] {
  return useSyncExternalStore(retryQueue.subscribe, retryQueue.snapshot, retryQueue.snapshot);
}

/** The queued item of one kind for one target (a visit, a consultation), if any. */
export function useQueuedAction(kind: QueuedKind, target: string): QueuedAction | undefined {
  return useRetryQueue().find((i) => i.kind === kind && i.target === target);
}

/** Sends what is waiting and refreshes what it changed. */
export function useFlushQueue(): () => void {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void retryQueue.flush().then(async ({ sent }) => {
      for (const item of sent) {
        for (const queryKey of item.refresh) await queryClient.invalidateQueries({ queryKey });
        if (item.sentTitle !== null) toasts.show({ title: item.sentTitle });
      }
    });
  }, [queryClient]);
}

/**
 * Loads this person's queue at this facility, sends it when the connection comes back and
 * every RETRY_INTERVAL_MS while anything waits, and forgets it on sign-out.
 */
export function RetryQueueRunner({ userId, facilityId }: { userId: string; facilityId: string }) {
  const online = useOnline();
  const flush = useFlushQueue();
  const waiting = useRetryQueue().some((i) => i.status === 'waiting');

  useEffect(() => {
    void sweepExpiredDrafts();
    void retryQueue.load(userId, facilityId).then(() => {
      if (navigator.onLine) flush();
    });
    return () => {
      retryQueue.forget();
    };
  }, [userId, facilityId, flush]);

  useEffect(() => {
    if (!online || !waiting) return undefined;
    flush();
    const timer = window.setInterval(flush, RETRY_INTERVAL_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [online, waiting, flush]);

  return null;
}

const plural = (n: number, one: string, many: string) => `${String(n)} ${n === 1 ? one : many}`;

export function offlineStripText(waiting: number, t: Translate = englishT): string {
  if (waiting === 0) return t('shell.offline');
  return t('shell.offlineWaiting', { count: actions(t, waiting) });
}

const englishT: Translate = (key, params) => translate('en', key, params);
const actions = (t: Translate, n: number) =>
  n === 1 ? t('shell.action') : t('shell.actions', { n: String(n) });

/** The strips under the top bar for the connection and the retry queue. */
export function OfflineStrips() {
  const t = useT();
  const online = useOnline();
  const items = useRetryQueue();
  const [reviewing, setReviewing] = useState(false);
  const waiting = items.filter((i) => i.status === 'waiting').length;
  const failed = items.filter((i) => i.status === 'failed').length;
  return (
    <>
      {online ? null : <StatusStrip>{offlineStripText(waiting, t)}</StatusStrip>}
      {online && waiting > 0 ? (
        <StatusStrip>{t('shell.sending', { count: actions(t, waiting) })}</StatusStrip>
      ) : null}
      {failed > 0 ? (
        <CriticalStrip
          action={
            <Button
              dense
              onPress={() => {
                setReviewing(true);
              }}
            >
              Review
            </Button>
          }
        >
          {`${plural(failed, 'action', 'actions')} saved on this device couldn't be sent to Wodi.`}
        </CriticalStrip>
      ) : null}
      <QueueReviewDialog isOpen={reviewing} onOpenChange={setReviewing} items={items} />
    </>
  );
}

function stateWords(item: QueuedAction): string {
  if (item.status === 'failed') return item.error?.message ?? 'Wodi refused it.';
  if (item.error !== null) return `Waiting to try again. Last time: ${item.error.message}`;
  return 'Waiting to send.';
}

function nextStep(item: QueuedAction): string {
  if (item.status !== 'failed') return 'It sends by itself when Wodi can be reached.';
  if (item.error?.code === EXPIRED_ERROR.code) {
    return "Open the patient's record in Wodi, enter it again if it is missing, then choose Don't send.";
  }
  return item.kind === 'triage'
    ? 'Open it: the entries are still in the form on this device. Fix them and save again.'
    : 'Open it: the note is still on this device, and saves again as you type.';
}

/** Every queued action with its state, reason and next step. */
export function QueueReviewDialog({
  isOpen,
  onOpenChange,
  items,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  items: readonly QueuedAction[];
}) {
  const flush = useFlushQueue();
  const navigate = useNavigate();
  return (
    <Dialog title="Saved on this device" isOpen={isOpen} onOpenChange={onOpenChange}>
      {items.length === 0 ? (
        <p className={styles.empty}>Everything saved on this device has been sent to Wodi.</p>
      ) : (
        <ul className={styles.list} aria-label="Actions saved on this device">
          {items.map((item) => (
            <li key={item.id} className={styles.item}>
              <div className={styles.itemHeading}>
                <strong>{item.label}</strong>
                <Tag state={item.status === 'failed' ? 'active' : 'pending'}>
                  {item.status === 'failed' ? 'Not sent' : 'Waiting'}
                </Tag>
              </div>
              <p className={styles.meta}>
                {`Saved on this device at ${formatEatTime(new Date(item.savedAt))}.`}
              </p>
              <p>{stateWords(item)}</p>
              <p className={styles.meta}>{nextStep(item)}</p>
              <div className={styles.actions}>
                <Button
                  dense
                  onPress={() => {
                    onOpenChange(false);
                    void navigate({ href: item.href });
                  }}
                >
                  Open
                </Button>
                {item.status === 'failed' &&
                item.error?.code !== 'alert_acknowledgement_required' &&
                item.error?.code !== EXPIRED_ERROR.code ? (
                  <Button
                    dense
                    onPress={() => {
                      void retryQueue.retry(item.id).then(flush);
                    }}
                  >
                    Try again
                  </Button>
                ) : null}
                <Button
                  dense
                  variant="quiet"
                  onPress={() => {
                    void retryQueue.remove(item.id);
                  }}
                >
                  Don't send
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <DialogActions>
        <Button
          onPress={() => {
            onOpenChange(false);
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
